import { useEffect, useRef, useState } from "react";
import SectionD from "./SectionD.jsx";
import PopularCourses from "./PopularCourses";
import { scrollHeaderIntoView } from "./utils/scrollToHeader";

const COURSE_HERO_STORAGE_KEY = "courseHeroOpen";

export default function CourseHero({ defaultOpen = false, isOpen: controlledOpen, onToggle }) {
  const isControlled = typeof controlledOpen === "boolean" && typeof onToggle === "function";
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const openAtRef = useRef(0);
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined") return defaultOpen;
    const stored = window.sessionStorage.getItem(COURSE_HERO_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return defaultOpen;
  });

  useEffect(() => {
    if (isControlled) return;
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(COURSE_HERO_STORAGE_KEY, isOpen ? "1" : "0");
  }, [isOpen, isControlled]);

  useEffect(() => {
    if (isControlled) return;
    return () => {
      if (typeof window === "undefined") return;
      window.sessionStorage.removeItem(COURSE_HERO_STORAGE_KEY);
    };
  }, [isControlled]);

  const open = isControlled ? controlledOpen : isOpen;
  const handleToggle = isControlled ? onToggle : () => setIsOpen((v) => !v);

  useEffect(() => {
    if (open) openAtRef.current = Date.now();
  }, [open]);

  useEffect(() => {
    if (!open || !headerRef.current || !contentRef.current) return;
    if (typeof window === "undefined") return;

    let raf = null;
    const observer = new ResizeObserver(() => {
      if (Date.now() - openAtRef.current > 8000) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        scrollHeaderIntoView(headerRef.current, "auto", 8);
      });
    });

    observer.observe(contentRef.current);
    const imgs = contentRef.current.querySelectorAll("img");
    const onImgLoad = () => {
      if (Date.now() - openAtRef.current > 8000) return;
      scrollHeaderIntoView(headerRef.current, "auto", 8);
    };
    imgs.forEach((img) => img.addEventListener("load", onImgLoad));
    return () => {
      observer.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open]);

  return (
    <div className="coursehero-area">
      <div className="mt-0">
        <SectionD onToggle={handleToggle} isOpen={open} headerRef={headerRef} />
      </div>
      <div
        ref={contentRef}
        className={`mt-0 overflow-hidden transform-gpu transition-[max-height,transform] duration-700 ease-out will-change-[max-height,transform] ${
          open
            ? "max-h-[2200px] translate-y-0"
            : "max-h-0 -translate-y-2 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <PopularCourses />
      </div>
    </div>
  );
}
